import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { isValidUuid } from '../XeroManualForm.helpers'

const STANDARD_INPUT_CLASS =
  'w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-[#4e5e82] focus:outline-none focus:ring-1 focus:ring-[#4e5e82] dark:border-stone-600 dark:bg-stone-700 dark:text-white'
const LABEL_CLASS = 'mb-1 block text-sm font-medium text-stone-600 dark:text-stone-400'

type ChangeHandler = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void

interface OrgIdFieldProps {
  value: string
  selectedOrgXeroId: string | undefined
  onChange: ChangeHandler
  t: TFunction<'admin'>
}

function orgIdInputClass(value: string, selectedOrgXeroId: string | undefined): string {
  if (selectedOrgXeroId) {
    return 'border-stone-300 bg-stone-100 dark:border-stone-600 dark:bg-stone-600 cursor-not-allowed'
  }
  if (value && !isValidUuid(value)) {
    return 'border-red-500 focus:border-red-500 focus:ring-red-500 dark:border-red-500'
  }
  return 'border-stone-300 focus:border-[#4e5e82] focus:ring-[#4e5e82] dark:border-stone-600'
}

export function OrgIdField({
  value,
  selectedOrgXeroId,
  onChange,
  t,
}: OrgIdFieldProps): ReactElement {
  const cls = orgIdInputClass(value, selectedOrgXeroId)
  const showInvalid = Boolean(value) && !selectedOrgXeroId && !isValidUuid(value)
  return (
    <div>
      <label htmlFor="xeroOrganisationId" className={LABEL_CLASS}>
        {t('licensing.xero.xeroOrgId')} *
        {selectedOrgXeroId && (
          <span className="ml-2 text-xs font-normal text-[#5c6e94] dark:text-[#8a9bba]">
            ({t('licensing.xero.fromSelectedOrg')})
          </span>
        )}
      </label>
      <input
        type="text"
        id="xeroOrganisationId"
        name="xeroOrganisationId"
        value={value}
        onChange={onChange}
        placeholder={t('licensing.xero.uuidPlaceholder')}
        required
        readOnly={Boolean(selectedOrgXeroId)}
        className={`w-full rounded-lg border px-3 py-2 text-sm focus:ring-1 focus:outline-none dark:bg-stone-700 dark:text-white ${cls}`}
      />
      {showInvalid && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
          {t('licensing.xero.invalidUuidFormat')}
        </p>
      )}
    </div>
  )
}

interface InvoiceNumberFieldProps {
  value: string
  onChange: ChangeHandler
  t: TFunction<'admin'>
}

export function InvoiceNumberField({ value, onChange, t }: InvoiceNumberFieldProps): ReactElement {
  return (
    <div>
      <label htmlFor="xeroInvoiceNumber" className={LABEL_CLASS}>
        {t('licensing.xero.invoiceNumber')} *
      </label>
      <input
        type="text"
        id="xeroInvoiceNumber"
        name="xeroInvoiceNumber"
        value={value}
        onChange={onChange}
        placeholder={t('licensing.xero.invoiceNumberPlaceholder')}
        required
        className={STANDARD_INPUT_CLASS}
      />
      <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
        {t('licensing.xero.invoiceNumberHint')}
      </p>
    </div>
  )
}

interface UuidFieldProps {
  id: string
  name: string
  value: string
  onChange: ChangeHandler
  label: string
  placeholder: string
}

export function UuidField({
  id,
  name,
  value,
  onChange,
  label,
  placeholder,
}: UuidFieldProps): ReactElement {
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
      </label>
      <input
        type="text"
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={STANDARD_INPUT_CLASS}
      />
    </div>
  )
}

interface AdvancedDetailsProps {
  tenantId: string
  invoiceId: string
  onChange: ChangeHandler
  t: TFunction<'admin'>
}

export function AdvancedDetails({
  tenantId,
  invoiceId,
  onChange,
  t,
}: AdvancedDetailsProps): ReactElement {
  return (
    <details className="rounded-lg border border-stone-200 dark:border-stone-700">
      <summary className="cursor-pointer px-4 py-2 text-sm font-medium text-stone-600 hover:bg-stone-50 dark:text-stone-400 dark:hover:bg-stone-700/50">
        {t('licensing.xero.advancedXeroUuids')}
      </summary>
      <div className="space-y-4 px-4 pt-2 pb-4">
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {t('licensing.xero.advancedXeroUuidsHint')}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <UuidField
            id="xeroTenantId"
            name="xeroTenantId"
            value={tenantId}
            onChange={onChange}
            label={t('licensing.xero.tenantId')}
            placeholder={t('licensing.xero.uuidPlaceholder')}
          />
          <UuidField
            id="xeroInvoiceId"
            name="xeroInvoiceId"
            value={invoiceId}
            onChange={onChange}
            label={t('licensing.xero.invoiceUuid')}
            placeholder={t('licensing.xero.uuidPlaceholder')}
          />
        </div>
      </div>
    </details>
  )
}
